import type { BuyPocketCardQuantity } from "@/lib/print-order-pricing";
import {
  isPhotoChallengeCardQuantity,
} from "@/lib/photo-challenge-card-pricing";
import type { PhotoChallengeImageUpload } from "@/lib/photo-challenge-card-contract";

export const PRINT_CART_STORAGE_VERSION = 1 as const;
export const PRINT_CART_STORAGE_KEY = "guestgallery:print-cart:v1";
export const PRINT_CART_DESIGN_COLLECTION_SLUG_MAX_LENGTH = 120;

export type PocketCardCartDesign = {
  eventId: number;
  itemSlug: string;
  itemVersion?: number;
  locale?: string;
  editableValues: Record<string, string>;
  backsideVariantId: string;
  backsideRole?: "text" | "monogram";
};

export type PhotoChallengeCardCartDesign = {
  eventId: number;
  itemSlug: string;
  itemVersion?: number;
  locale?: string;
  title: string;
  tasks: string[];
  scanText?: string;
  hosts?: string;
  editableValues?: Record<string, string>;
  imageUploads?: Record<string, PhotoChallengeImageUpload>;
};

function isPhotoChallengeImageUploads(value: unknown): value is Record<string, PhotoChallengeImageUpload> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  if (entries.length > 12) return false;
  return entries.every(([fieldId, upload]) => {
    if (!fieldId || fieldId.length > 120 || typeof upload !== "object" || upload === null || Array.isArray(upload)) return false;
    const candidate = upload as Partial<PhotoChallengeImageUpload>;
    if (
      (typeof candidate.uploadId !== "undefined" && typeof candidate.uploadId !== "string") ||
      (typeof candidate.storageKey !== "undefined" && typeof candidate.storageKey !== "string") ||
      (!candidate.uploadId && !candidate.storageKey)
    ) return false;
    if (candidate.crop === undefined) return true;
    const crop = candidate.crop;
    return (
      typeof crop === "object" && crop !== null &&
      Number.isFinite(crop.x) && crop.x >= 0 && crop.x <= 1 &&
      Number.isFinite(crop.y) && crop.y >= 0 && crop.y <= 1 &&
      (crop.zoom === undefined || (Number.isFinite(crop.zoom) && crop.zoom >= 1 && crop.zoom <= 10))
    );
  });
}

export type PocketCardCartItem = {
  type: "pocket-cards";
  quantity: BuyPocketCardQuantity;
  designCollectionSlug?: string;
  design: PocketCardCartDesign;
};

export type PhotoChallengeCardCartItem = {
  type: "photo-challenge-cards";
  quantity: number;
  designCollectionSlug?: string;
  design: PhotoChallengeCardCartDesign;
};

export type PrintCartItem =
  | PocketCardCartItem
  | PhotoChallengeCardCartItem;

export type PrintCart = {
  version: typeof PRINT_CART_STORAGE_VERSION;
  items: PrintCartItem[];
};

export type PrintCartCheckoutPayload = {
  checkoutAttemptId: string;
  locale: string;
  items: PrintCartCheckoutItem[];
};

export type PrintCartCheckoutItem = Omit<
  PrintCartItem,
  "designCollectionSlug"
>;

/** Remove cart-only presentation metadata before sending checkout data. */
export function withoutPrintCartDesignMetadata(
  item: PrintCartItem,
): PrintCartCheckoutItem {
  const { designCollectionSlug: _designCollectionSlug, ...checkoutItem } = item;
  return checkoutItem;
}

function isDesignCollectionSlug(value: unknown): value is string | undefined {
  return (
    typeof value === "undefined" ||
    (typeof value === "string" &&
      value.trim().length > 0 &&
      value.length <= PRINT_CART_DESIGN_COLLECTION_SLUG_MAX_LENGTH)
  );
}

export function isPrintCartItem(value: unknown): value is PrintCartItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<PrintCartItem>;
  if (!item.design || typeof item.design !== "object") return false;
  if (item.type === "pocket-cards") {
    return (
      isDesignCollectionSlug(item.designCollectionSlug) &&
      (item.quantity === 50 || item.quantity === 75 || item.quantity === 100) &&
      typeof item.design.eventId === "number" &&
      Number.isInteger(item.design.eventId) &&
      typeof item.design.itemSlug === "string" &&
      typeof item.design.editableValues === "object" &&
      item.design.editableValues !== null &&
      Object.values(item.design.editableValues).every(
        (value) => typeof value === "string",
      ) &&
      typeof item.design.backsideVariantId === "string" &&
      (typeof item.design.backsideRole === "undefined" ||
        item.design.backsideRole === "text" ||
        item.design.backsideRole === "monogram")
    );
  }
  if (item.type === "photo-challenge-cards") {
    return (
      isDesignCollectionSlug(item.designCollectionSlug) &&
      typeof item.quantity === "number" &&
      isPhotoChallengeCardQuantity(item.quantity) &&
      typeof item.design.eventId === "number" &&
      Number.isInteger(item.design.eventId) &&
      typeof item.design.itemSlug === "string" &&
      typeof item.design.title === "string" &&
      (typeof item.design.scanText === "undefined" ||
        typeof item.design.scanText === "string") &&
      (typeof item.design.hosts === "undefined" ||
        typeof item.design.hosts === "string") &&
      (typeof item.design.editableValues === "undefined" ||
        (typeof item.design.editableValues === "object" &&
          item.design.editableValues !== null &&
          Object.values(item.design.editableValues).every(
            (value) => typeof value === "string",
          ))) &&
      (typeof item.design.imageUploads === "undefined" ||
        isPhotoChallengeImageUploads(item.design.imageUploads)) &&
      Array.isArray(item.design.tasks) &&
      item.design.tasks.every((task) => typeof task === "string")
    );
  }
  return false;
}

export function isPrintCart(value: unknown): value is PrintCart {
  if (!value || typeof value !== "object") return false;
  const cart = value as Partial<PrintCart>;
  if (
    cart.version === PRINT_CART_STORAGE_VERSION &&
    Array.isArray(cart.items) &&
    cart.items.length > 0 &&
    cart.items.length <= 2 &&
    cart.items.every(isPrintCartItem)
  ) {
    const eventIds = cart.items.map((item) => item.design.eventId);
    const types = cart.items.map((item) => item.type);
    return (
      eventIds.every((eventId) => Number.isInteger(eventId) && eventId > 0) &&
      new Set(eventIds).size === 1 &&
      new Set(types).size === types.length
    );
  }
  // An empty cart is a valid current state, but all persisted entries must have
  // passed the shape checks above before hydration.
  return cart.version === PRINT_CART_STORAGE_VERSION &&
    Array.isArray(cart.items) &&
    cart.items.length === 0;
}

/** Stable enough for browser cart de-duplication; no crypto or pricing data. */
export function getPrintCartItemKey(item: PrintCartItem): string {
  const json = JSON.stringify(item);
  let hash = 2166136261;
  for (let index = 0; index < json.length; index += 1) {
    hash ^= json.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${item.type}:${(hash >>> 0).toString(36)}`;
}
